import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Handle missing environment variables gracefully
if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('⚠️  Supabase environment variables not configured. Database features will not work.');
  console.log('📝 To fix this, create a .env.local file with your Supabase credentials. See ENVIRONMENT_SETUP.md for details.');
}

import { mapDatabaseIncentive } from './databaseMapping.js';

// Create Supabase client only if environment variables are available
export const supabase = supabaseUrl && supabaseAnonKey
  ? createClient(supabaseUrl, supabaseAnonKey)
  : null;

// Database helper functions
export const dbHelpers = {
  // Incentives - Updated to use actual database schema
  async getIncentives(filters = {}) {
    if (!supabase) {
      throw new Error('Supabase not configured. Please set up environment variables.');
    }

    let query = supabase.from('incentives_global_2025').select('*');

    // Apply filters using actual database column names
    if (filters.jurisdiction) {
      query = query.eq('jurisdiction', filters.jurisdiction);
    }

    if (filters.type) {
      query = query.eq('type', filters.type);
    }

    const { data, error } = await query;
    if (error) throw error;
    
    // Map database records to expected interface
    return data?.map(mapDatabaseIncentive) || [];
  },

  // Get all incentives for matching algorithm
  async getAllIncentives() {
    if (!supabase) {
      console.warn('⚠️ Supabase not configured. Please set up environment variables.');
      throw new Error('Supabase not configured. Please set up environment variables.');
    }

    console.log('🔍 Fetching all incentives from incentives_global_2025 table...');

    // Try multiple table names to find the incentives data
    let data, error;
    const tableNames = ['incentives_global_2025', 'incentives'];
    
    for (const tableName of tableNames) {
      try {
        console.log(`🔍 Trying table: ${tableName}`);
        const result = await supabase
          .from(tableName)
          .select('*')
          .order('value', { ascending: false }); // Use 'value' instead of 'mark_stars'
        
        data = result.data;
        error = result.error;
        
        if (!error && data && data.length > 0) {
          console.log(`✅ Found data in table: ${tableName} (${data.length} records)`);
          break;
        } else if (error) {
          console.log(`❌ Error with table ${tableName}:`, error.message);
        } else {
          console.log(`⚠️ Table ${tableName} exists but is empty`);
        }
      } catch (queryError) {
        console.log(`❌ Query failed for table ${tableName}:`, queryError.message);
      }
    }

    if (error) {
      console.error('❌ Supabase query error:', error);
      throw error;
    }

          console.log(`✅ Successfully fetched ${data?.length || 0} incentives from database`);
      console.log('📊 Sample raw database data:', data?.slice(0, 2));

      // Verify this is actual database data, not mock data
      if (data && data.length > 0) {
        const sampleRecord = data[0];
        console.log('🔍 Database record structure:', {
          hasJurisdiction: !!sampleRecord.jurisdiction,
          hasNameOfIncentive: !!sampleRecord.name_of_incentive,
          hasValue: !!sampleRecord.value,
          hasType: !!sampleRecord.type,
          sampleValues: {
            jurisdiction: sampleRecord.jurisdiction,
            name: sampleRecord.name_of_incentive,
            value: sampleRecord.value,
            type: sampleRecord.type
          }
        });
      }

      // DEBUG: Log actual incentive IDs to help with foreign key issue
      console.log('🎯 Sample incentive IDs from DB:', data?.slice(0, 3).map(item => ({
        id: item.id,
        jurisdiction: item.jurisdiction,
        type: typeof item.id
      })));

      // Map database records to expected interface
      const mappedData = data?.map(mapDatabaseIncentive) || [];
      console.log('📊 Sample mapped data:', mappedData?.slice(0, 2));

      return mappedData;
  },

  async updateIncentive(id, updates) {
    if (!supabase) {
      throw new Error('Supabase not configured. Please set up environment variables.');
    }

    const { data, error } = await supabase
      .from('incentives')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select();

    if (error) throw error;
    return data[0];
  },

  // Submissions
  async createSubmission(submissionData) {
    if (!supabase) {
      throw new Error('Supabase not configured. Please set up environment variables.');
    }

    const { data, error } = await supabase
      .from('submissions')
      .insert(submissionData)
      .select();

    if (error) throw error;
    return data[0];
  },

  async getSubmission(id) {
    if (!supabase) {
      throw new Error('Supabase not configured. Please set up environment variables.');
    }

    const { data, error } = await supabase
      .from('submissions')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  // Report Requests
  async createReportRequest(reportData) {
    if (!supabase) {
      throw new Error('Supabase not configured. Please set up environment variables.');
    }

    const { data, error } = await supabase
      .from('report_requests')
      .insert(reportData)
      .select();

    if (error) throw error;
    return data[0];
  },

  async updateReportRequest(id, updates) {
    if (!supabase) {
      throw new Error('Supabase not configured. Please set up environment variables.');
    }

    const { data, error } = await supabase
      .from('report_requests')
      .update(updates)
      .eq('id', id)
      .select();

    if (error) throw error;
    return data[0];
  },

  // Match Results
  async createMatchResults(results) {
    if (!supabase) {
      throw new Error('Supabase not configured. Please set up environment variables.');
    }

    const { data, error } = await supabase
      .from('match_results')
      .insert(results)
      .select();

    if (error) throw error;
    return data;
  },

  async getMatchResults(submissionId) {
    if (!supabase) {
      throw new Error('Supabase not configured. Please set up environment variables.');
    }

    const { data, error } = await supabase
      .from('match_results')
      .select(`
        *,
        incentive:incentives(*)
      `)
      .eq('submission_id', submissionId)
      .order('rank');

    if (error) throw error;
    return data;
  },

  // Users
  async createUser(userData) {
    if (!supabase) {
      throw new Error('Supabase not configured. Please set up environment variables.');
    }

    const { data, error } = await supabase
      .from('users_min')
      .insert(userData)
      .select();

    if (error) throw error;
    return data[0];
  },

  // Share Links
  async createShareLink(submissionId, expiresAt) {
    const shareToken = Math.random().toString(36).substr(2, 16);
    
    const { data, error } = await supabase
      .from('share_links')
      .insert({
        submission_id: submissionId,
        share_token: shareToken,
        expires_at: expiresAt,
        one_time: false
      })
      .select();
    
    if (error) throw error;
    return data[0];
  },

  async getShareLink(shareToken) {
    const { data, error } = await supabase
      .from('share_links')
      .select('*')
      .eq('share_token', shareToken)
      .single();
    
    if (error) throw error;
    
    // Check if expired
    if (new Date(data.expires_at) < new Date()) {
      throw new Error('Share link has expired');
    }
    
    return data;
  },

  // Scoring Weights
  async getScoringWeights() {
    const { data, error } = await supabase
      .from('scoring_weights')
      .select('*')
      .eq('active', true)
      .single();
    
    if (error) throw error;
    return data;
  },

  // Audit Logs
  async createAuditLog(actor, action, payload) {
    const { data, error } = await supabase
      .from('audit_logs')
      .insert({
        actor,
        action,
        payload
      });
    
    if (error) throw error;
    return data;
  }
};

// File storage helpers
export const storageHelpers = {
  async uploadFile(bucket, path, file) {
    const { data, error } = await supabase.storage
      .from(bucket)
      .upload(path, file);
    
    if (error) throw error;
    return data;
  },

  async getFileUrl(bucket, path) {
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(path);
    
    return data.publicUrl;
  },

  async deleteFile(bucket, path) {
    const { error } = await supabase.storage
      .from(bucket)
      .remove([path]);
    
    if (error) throw error;
  }
};